import { useEffect, useState } from "react";
import {  Container, Grid, TextField } from "@mui/material";
import { useQuery } from "react-query";
import { DatePicker, LoadingButton } from "@mui/lab";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";
import { schoolList } from "../api";
import { useSnackbar } from "notistack";
import axiosInstance from "src/utils/axios";
import { useForm, FormProvider, Controller } from "react-hook-form";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";


export default function GSTReport({ type }) {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const methods = useForm();
  const { setValue, watch, control } = methods;

  const selectedSchool = watch("selectedSchool");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loadingLabel, setLoadingLabel] = useState(null);

  const { data } = useQuery({
    queryKey: ["schoolList"],
    queryFn: schoolList,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const fetchdata = async () => {
    if (selectedSchool && fromDate && toDate) {
      try {
        setLoadingLabel(true);
        let reqUrl = "/report/vendor/salesWithOutGst";
        let outputFilename = `without-gst-${new Date().toISOString()}.xlsx`;
        if (type === "withGst") {
          reqUrl = "report/vendor/salesWithGst";
          outputFilename = `with-gst-${new Date().toISOString()}.xlsx`;
        }
        if (type === "shipping") {
          reqUrl = "report/vendor/salesShipping";
          outputFilename = `shipping-${new Date().toISOString()}.xlsx`;
        }

        const config = {
          method: "GET",
          url: `${reqUrl}?schoolID=${selectedSchool.schoolID}&initialDate=${
            new Date(fromDate).toISOString().split("T")[0]
          }&endDate=${new Date(toDate).toISOString().split("T")[0]}`,
          responseType: "blob",
        };

        const { data } = await axiosInstance(config);
        const url = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        enqueueSnackbar("No data found", { variant: "error" });
      } finally {
        setLoadingLabel(false);
      }
    } else {
      enqueueSnackbar("Please select all fields", { variant: "error" });
    }
  };

  useEffect(() => {
    setToDate(null);
    setFromDate(null);
    setValue("selectedSchool", null);
  }, [type]);

  return (
    <Page title="Report">
      <Container maxWidth={themeStretch ? false : "xl"}>
      <HeaderBreadcrumbs
         heading={`${
          type === "withGst"
            ? "With GST"
            : type === "shipping"
            ? "Shipping"
            : "Without GST"
        }`}        
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Report" },
            {
              name: `
                ${
                  type === "withGst"
                    ? "With GST"
                    : type === "shipping"
                    ? "Shipping"
                    : "Without GST"
                }`,
            },
          ]}
        />
         <FormProvider {...methods}>
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12}>
          <Controller
                name="selectedSchool"
                control={control}
                render={({ field }) => (
                  <RHFAutocomplete
                    {...field}
                    label="School"
                    options={data?.data || []}
                    getOptionLabel={(option) => option.schoolName || ""}
                    onChange={(_, value) => setValue("selectedSchool", value)}
                  />
                )}
              />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Order From"
              value={fromDate}
              clearable
              disabled={!selectedSchool}
              onChange={value => {
                setFromDate(value);
                setToDate(null);
              }}
              disableFuture
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onKeyDown={e => {
                    e.preventDefault();
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Order to"
              value={toDate}
              clearable
              onChange={value => {
                setToDate(value);
              }}
              disableFuture
              disabled={!fromDate}
              minDate={fromDate}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onKeyDown={e => {
                    e.preventDefault();
                  }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loadingLabel}
              onClick={() => {
                fetchdata();
              }}
            >
              Load Data
            </LoadingButton>
          </Grid>
        </Grid>
        </FormProvider>
      </Container>
    </Page>
  );
}
